import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  ResponsiveContainer
} from 'recharts';
import { useEffect, useState } from 'react';
import axiosInstance from '../../utils/axiosInstance';
import Loading from '../common/Loading';
import dayjs from "dayjs";

function formatMonth(ym) {
  try {
    const [year, month] = ym.split("-");
    const date = new Date(year, month - 1);
    return date.toLocaleString("en-US", { month: "short", year: "numeric" });
  } catch (e) {
    return ym;
  }
}

export default function IncomeExpenseWidget({ period = '12m' }) {
  const [data, setData] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(false);
  const [selectedPeriod, setSelectedPeriod] = useState(period);

  const periodMonths = { '3m': 2, '6m': 5, '12m': 11 };

  useEffect(() => {
    setLoading(true);
    setError(false);
    const months = periodMonths[selectedPeriod] || 11;
    const startDate = dayjs().subtract(months, "month").startOf("month").format("YYYY-MM-DD");
    const endDate = dayjs().endOf("month").format("YYYY-MM-DD");

    axiosInstance.get(`/expense/income-expense?startDate=${startDate}&endDate=${endDate}`)
      .then(res => {
        if (res.data.success) {
          const monthlyData = res.data.data.map(item => ({
            name: formatMonth(item.month),
            income: item.income,
            expense: item.expense * -1,
            total: item.income - item.expense
          }));
          setData(monthlyData);
        }
      })
      .catch(err => {
        console.error("Failed to load monthly summary", err);
        setError(true);
      })
      .finally(() => setLoading(false));
  }, [selectedPeriod]);

  if (error) {
    return (
      <div className="p-4 bg-red-50 border border-red-200 rounded-lg text-center">
        <p className="text-red-600 text-sm">Failed to load chart data</p>
        <button
          onClick={() => { setError(false); setLoading(true); setSelectedPeriod(selectedPeriod); }}
          className="mt-2 text-sm text-red-700 underline hover:no-underline"
        >
          Retry
        </button>
      </div>
    );
  }

  return (
    <div className="p-4">
      {/* Period selector */}
      <div className="flex justify-end mb-4">
        <div className="inline-flex rounded-md border border-gray-300 overflow-hidden">
          {[
            { label: '3 Months', value: '3m' },
            { label: '6 Months', value: '6m' },
            { label: '12 Months', value: '12m' },
          ].map(opt => (
            <button
              key={opt.value}
              onClick={() => setSelectedPeriod(opt.value)}
              className={`px-3 py-1.5 text-sm font-medium transition-colors ${
                selectedPeriod === opt.value
                  ? 'bg-blue-600 text-white'
                  : 'bg-white text-gray-700 hover:bg-gray-50'
              }`}
            >
              {opt.label}
            </button>
          ))}
        </div>
      </div>

      {loading ? (
        <Loading size="sm" />
      ) : (
        <ResponsiveContainer width="100%" height={280}>
          <BarChart data={data} stackOffset="sign">
            <XAxis dataKey="name" tick={{ fontSize: 12 }} />
            <YAxis tick={{ fontSize: 12 }} />
            <CartesianGrid stroke="#e5e7eb" strokeDasharray="3 3" />
            <Tooltip
              formatter={(value) =>
                `$${Math.abs(value).toLocaleString('en-US', { minimumFractionDigits: 2 })}`
              }
            />
            <Legend />
            <Bar dataKey="income" fill="#22c55e" stackId="stack" barSize={40} radius={[2, 2, 0, 0]} />
            <Bar dataKey="expense" fill="#ef4444" stackId="stack" />
            <Bar dataKey="total" fill="#3b82f6" stackId="stack" name="Net" />
          </BarChart>
        </ResponsiveContainer>
      )}
    </div>
  );
}
